import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";
import { Banner, Button, Modal, SegmentedControl, Select, StatusDot, useToast } from "@/shared/rd";
import type { StoreThemeJson, TemplateName } from "@a2z/storefront-schemas";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ThemeValidationErrorItem, ThemeVersion } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoresQuery } from "@/shared/stores/services/queries/useStoresQuery";
import { useUpdateThemeVersionJsonMutation } from "../services/useUpdateThemeVersionJsonMutation";
import { usePublishThemeVersionMutation } from "../services/usePublishThemeVersionMutation";
import { SectionList } from "./components/SectionList";
import { SectionInspector } from "./components/SectionInspector";
import { StudioPreview, type PreviewDevice } from "./components/StudioPreview";
import { RawJsonPanel } from "./components/RawJsonPanel";
import { BrandMotionPanel } from "./components/BrandMotionPanel";
import { ThumbnailField } from "./components/ThumbnailField";
import { useThemeDraft } from "./state/useThemeDraft";

/**
 * The visual catalog-theme editor. Replaces the raw-JSON textarea that used to
 * be the whole of `ThemeVersionEditor` (which is still reachable, folded into
 * the disclosure at the bottom — see `RawJsonPanel` for why removing it would
 * be a regression).
 *
 * Three panes: section list │ live storefront preview │ inspector.
 *
 * Design notes worth reading before changing this:
 *  - Preview strategy and why the seller's token mechanism does not transfer:
 *    `docs/plans/2026-07-20-admin-theme-studio.md` §1.
 *  - What is shared with the seller editor vs admin-only, and the extraction
 *    plan that removes the remaining duplication: §2 of the same doc.
 */

const TEMPLATES: TemplateName[] = ["home", "product", "collection", "cart"];
const DEVICES: PreviewDevice[] = ["desktop", "tablet", "mobile"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function extractValidationErrors(payload: unknown): ThemeValidationErrorItem[] {
  if (!isRecord(payload) || !Array.isArray(payload.errors)) return [];
  return payload.errors.filter(
    (item): item is ThemeValidationErrorItem =>
      isRecord(item) &&
      typeof item.path === "string" &&
      typeof item.code === "string" &&
      typeof item.message === "string",
  );
}

export function ThemeStudio({
  themeId,
  version,
  heading,
  locale,
  detailsPanel,
  readOnly = false,
  onCreateVersion,
  isCreatingVersion = false,
}: {
  themeId: string;
  version: ThemeVersion;
  /** The studio card's title ("Version 4 · Draft"). */
  heading?: string;
  locale: "ar" | "en";
  /**
   * View a PUBLISHED version. A published version is immutable server-side
   * (theme-catalog.service refuses json edits), so the studio still shows the
   * section list + preview + inspector — you can SEE every section — but Save/
   * Publish are replaced by a "create a new version to edit" affordance, and the
   * list/inspector panes are non-interactive so nothing edited here is silently
   * lost. This is what makes selecting a published-only theme show its sections
   * instead of just the metadata form.
   */
  readOnly?: boolean;
  /** Called by the read-only banner's edit button (creates a draft version). */
  onCreateVersion?: () => void;
  isCreatingVersion?: boolean;
  /**
   * The theme's metadata form, revealed by a toolbar toggle rather than
   * occupying the top of the page permanently.
   *
   * Passed in rather than imported so the studio does not depend on
   * `ThemeEditForm`: the page owns the theme record and its mutations, the
   * studio owns the toolbar and the layout. That keeps the studio usable
   * anywhere a theme version is edited, with or without a metadata form.
   */
  detailsPanel?: ReactNode;
}) {
  const t = useTranslations();
  // Past-tense confirmations go to the shell's toast. Every handler checks
  // `mounted` first, so a late response after the admin left shows nothing.
  const toast = useToast();

  const initial = version.json as unknown as StoreThemeJson;
  const draft = useThemeDraft(initial);
  const guard = useNavigationGuard();
  const setDirtySource = guard?.setDirtySource;
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useLayoutEffect(() => {
    setDirtySource?.("content", !readOnly && draft.isDirty);
    return () => setDirtySource?.("content", false);
  }, [draft.isDirty, readOnly, setDirtySource]);

  const [device, setDevice] = useState<PreviewDevice>("desktop");
  // Closed by default: the metadata is set once when the theme is created and
  // rarely touched after, whereas the three panes are the working surface.
  const [showDetails, setShowDetails] = useState(false);
  const [storeSlug, setStoreSlug] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    ThemeValidationErrorItem[]
  >([]);
  const [isPublishOpen, setIsPublishOpen] = useState(false);

  const { mutateAsync: saveJson, isPending: isSaving } =
    useUpdateThemeVersionJsonMutation();
  const { mutateAsync: publish, isPending: isPublishing } =
    usePublishThemeVersionMutation();

  // Render-target candidates. Reference stores first — they are the ones seeded
  // with real depth (categories AND products AND imagery), which is what a
  // theme's `$category:N` / `$products:N` slots need to resolve. A thin store
  // silently drops sections and makes the theme look broken.
  // Every store, deactivated ones included (labelled): an admin may preview a
  // theme against a store that is switched off.
  const storesQuery = useStoresQuery({ limit: 100, status: "all" });
  const storeOptions = useMemo(() => {
    const stores = storesQuery.data?.items ?? [];
    return [...stores].sort((a, b) => {
      const aRef = a.storeSlug.startsWith("demo-") ? 0 : 1;
      const bRef = b.storeSlug.startsWith("demo-") ? 0 : 1;
      if (aRef !== bRef) return aRef - bRef;
      return a.storeSlug.localeCompare(b.storeSlug);
    });
  }, [storesQuery.data]);

  const effectiveStoreSlug = storeSlug ?? storeOptions[0]?.storeSlug ?? null;

  const handleSave = async () => {
    if (!mounted.current) return;
    setSaveError(null);
    setPublishError(null);
    setValidationErrors([]);
    try {
      const updated = await saveJson({
        themeId,
        versionId: version.id,
        schemaVersion: draft.draft.schemaVersion,
        settings: draft.draft.settings as unknown as Record<string, unknown>,
        templates: draft.draft.templates as unknown as Record<string, unknown>,
        pages: draft.draft.pages as unknown as Record<string, unknown>,
      });
      if (!mounted.current) return;
      // Re-baseline from the SERVER's copy, not the local draft: the backend
      // may normalise on write, and baselining from the local object would
      // leave `isDirty` false while the two actually differ.
      draft.markSaved(updated.json as unknown as StoreThemeJson);
      // The preview reads the persisted row, so it is only now that a reload
      // shows the new state.
      setReloadKey((k) => k + 1);
      toast.show({ message: t(keys.themes.saveSuccess) });
    } catch (error) {
      if (!mounted.current) return;
      if (error instanceof ApiJsonClientError) {
        setSaveError(error.message || "");
        setValidationErrors(extractValidationErrors(error.payload));
      } else {
        setSaveError("");
      }
    }
  };

  const handlePublish = async () => {
    if (!mounted.current) return;
    setSaveError(null);
    setPublishError(null);
    setValidationErrors([]);
    try {
      await publish({ themeId, versionId: version.id });
      if (!mounted.current) return;
      setIsPublishOpen(false);
      toast.show({ message: t(keys.themes.publishSuccess) });
    } catch (error) {
      if (!mounted.current) return;
      // The failure (and any validation list) shows above the studio, where
      // the JSON it is about is edited; its Try again publishes directly.
      setIsPublishOpen(false);
      if (error instanceof ApiJsonClientError) {
        setPublishError(error.message || "");
        setValidationErrors(extractValidationErrors(error.payload));
      } else {
        setPublishError("");
      }
    }
  };

  const label = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
  const pane = (interactive: boolean): CSSProperties => ({
    minWidth: 0,
    overflow: "hidden",
    borderRadius: 8,
    boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
    background: "var(--rd-surface)",
    ...(interactive ? null : { pointerEvents: "none", opacity: 0.9 }),
  });

  return (
    <fieldset
      disabled={isSaving || isPublishing}
      aria-busy={isSaving || isPublishing}
      style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0, margin: 0, padding: 0, border: 0 }}
    >
      {publishError !== null || saveError !== null ? (
        <Banner
          tone="error"
          title={t(publishError !== null ? "themes.publishFailedTitle" : "themes.saveVersionFailedTitle")}
          actions={
            <Button
              size="sm"
              loading={isSaving || isPublishing}
              loadingLabel={t(keys.shell.retrying)}
              onClick={() => void (publishError !== null ? handlePublish() : handleSave())}
            >
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {[
            publishError ?? saveError,
            t(publishError !== null ? "themes.publishNothingChanged" : "themes.saveVersionNothingChanged"),
          ]
            .filter(Boolean)
            .join(" ")}
        </Banner>
      ) : null}

      {validationErrors.length > 0 ? (
        <Banner tone="error" title={t(keys.themes.publishValidationTitle)}>
          <ul style={{ margin: "4px 0 0", paddingInlineStart: 18, display: "flex", flexDirection: "column", gap: 4 }}>
            {validationErrors.map((item, index) => (
              <li key={`${item.path}-${item.code}-${index}`}>
                <span dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600 }}>
                  {item.path}
                </span>
                {" — "}
                {item.message}
              </li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <section className="rd-card" style={{ overflow: "hidden" }}>
        {/* Toolbar: the card's header. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 20px",
            borderBottom: "1px solid var(--rd-line-row)",
            flexWrap: "wrap",
          }}
        >
          {heading ? <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{heading}</h2> : null}

          <SegmentedControl<TemplateName>
            label={t(keys.themes.studioTemplate)}
            options={TEMPLATES.map((name) => ({ value: name, label: label(name) }))}
            value={draft.activeTemplate}
            onChange={(name) => draft.setActiveTemplate(name)}
          />

          <Select
            size="sm"
            aria-label={t(keys.themes.studioPreviewStore)}
            value={effectiveStoreSlug ?? ""}
            onChange={(e) => setStoreSlug(e.target.value)}
            options={storeOptions.map((store) => ({
              value: store.storeSlug,
              label: store.deactivatedAt
                ? `${store.storeSlug} (${t(keys.storeControls.statusDeactivated)})`
                : store.storeSlug,
            }))}
            style={{ maxWidth: 220 }}
          />

          <SegmentedControl<PreviewDevice>
            options={DEVICES.map((name) => ({ value: name, label: label(name) }))}
            value={device}
            onChange={setDevice}
          />

          {/* A view toggle, not an action: `aria-pressed` reports the open
              state. Reuses the metadata title as its label. */}
          {detailsPanel ? (
            <Button
              variant="subtle"
              size="sm"
              aria-pressed={showDetails}
              aria-controls="theme-details-panel"
              onClick={() => setShowDetails((open) => !open)}
            >
              {t(keys.themes.metadataTitle)}
            </Button>
          ) : null}

          <div style={{ flex: 1 }} />

          {readOnly ? (
            // Published version: Save/Publish don't apply (it's immutable). Offer
            // the one action that does — branch a new editable draft.
            onCreateVersion ? (
              <Button
                variant="primary"
                size="sm"
                loading={isCreatingVersion}
                loadingLabel={t("themes.creatingVersion")}
                onClick={onCreateVersion}
              >
                {t(keys.themes.newVersion)}
              </Button>
            ) : null
          ) : (
            <>
              {draft.isDirty ? <StatusDot tone="amber">{t(keys.themes.studioUnsaved)}</StatusDot> : null}
              <Button
                variant="secondary"
                size="sm"
                disabled={!draft.isDirty}
                loading={isSaving}
                loadingLabel={t("themes.saving")}
                onClick={() => void handleSave()}
              >
                {t(keys.themes.studioSave)}
              </Button>
              <Button
                variant="primary"
                size="sm"
                // Publish freezes the last-SAVED server json, not the editor
                // state. Blocking on dirty is what stops unsaved edits from being
                // silently discarded into an immutable version.
                disabled={draft.isDirty}
                loading={isPublishing}
                loadingLabel={t("themes.publishing")}
                onClick={() => {
                  setPublishError(null);
                  setValidationErrors([]);
                  setIsPublishOpen(true);
                }}
              >
                {t(keys.themes.publish)}
              </Button>
            </>
          )}
        </div>

        {readOnly ? (
          <p
            style={{
              margin: 0,
              padding: "10px 20px",
              borderBottom: "1px solid var(--rd-line-row)",
              background: "var(--rd-surface-soft)",
              fontSize: 13,
              color: "var(--rd-text-secondary)",
            }}
          >
            {t(keys.themes.studioReadOnlyNote)}
          </p>
        ) : null}

        {/* Closing the panel preserves its local draft. Hidden controls are also
            disabled, so they cannot be focused or submitted while out of view. */}
        {detailsPanel ? (
          <div
            id="theme-details-panel"
            hidden={!showDetails}
            style={{ padding: "16px 20px", borderBottom: "1px solid var(--rd-line-row)" }}
          >
            <fieldset disabled={!showDetails} style={{ display: "contents" }}>
              {detailsPanel}
            </fieldset>
          </div>
        ) : null}

        {/* Three panes. In read-only, the list + inspector are non-interactive
            (a published version can't be edited here) so they are for VIEWING;
            the preview stays fully interactive. They wrap on narrow screens. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: 12 }}>
          <div
            style={{ ...pane(!readOnly), flex: "1 1 200px", maxWidth: 260, height: "70vh", minHeight: 520 }}
            aria-disabled={readOnly || undefined}
          >
            <SectionList draft={draft} lang={locale} />
          </div>

          <div style={{ ...pane(true), flex: "999 1 420px", height: "70vh", minHeight: 520 }}>
            <StudioPreview
              themeId={themeId}
              versionId={version.id}
              storeSlug={effectiveStoreSlug}
              device={device}
              locale={locale}
              reloadKey={reloadKey}
            />
          </div>

          <div
            style={{ ...pane(!readOnly), flex: "1 1 240px", maxWidth: 320, height: "70vh", minHeight: 520 }}
            aria-disabled={readOnly || undefined}
          >
            <SectionInspector draft={draft} lang={locale} />
          </div>
        </div>
      </section>

      <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-meta)" }}>{t(keys.themes.studioPreviewNote)}</p>

      {/* Thumbnail is version METADATA, not part of the rendered payload, so the
          backend allows writing it even on a published (frozen) version (see
          updateVersionMeta). It therefore renders regardless of readOnly. */}
      <ThumbnailField themeId={themeId} version={version} />

      {/* Editors below the panes need a mutable draft — hidden when viewing a
          published version. Create a new version to reach them. */}
      {readOnly ? null : (
        <>
          {/* Theme-level MOTION control (scroll-reveal driver). Brand-wide, so
              it lives outside the per-section inspector — see BrandMotionPanel. */}
          <BrandMotionPanel draft={draft} lang={locale} />

          <RawJsonPanel draft={draft} />
        </>
      )}

      <Modal
        open={isPublishOpen}
        onClose={isPublishing ? undefined : () => setIsPublishOpen(false)}
        title={t(keys.themes.publishConfirmTitle)}
        width={420}
        actions={
          <>
            <Button variant="ghost" muted disabled={isPublishing} onClick={() => setIsPublishOpen(false)}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant="primary"
              loading={isPublishing}
              loadingLabel={t("themes.publishing")}
              onClick={() => void handlePublish()}
            >
              {t(keys.themes.publish)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.themes.publishConfirmBody)}
        </p>
      </Modal>
    </fieldset>
  );
}
